import React from 'react';
import DocLayout from '../DocLayout';
import { GitBranch, TrendingUp } from 'lucide-react';

const PipelineStages = () => {
  return (
    <DocLayout title="Lead Status & Pipeline Stages" category="Lead Management">
      <div className="space-y-6">
        <section>
          <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
            <GitBranch className="w-6 h-6 text-indigo-600" />
            Understanding Your Sales Pipeline
          </h2>
          <p className="text-slate-700 mb-4">
            I-Match uses a stage-based pipeline to help you track leads from first contact to closed deal.
          </p>
        </section>

        <section>
          <h3 className="text-xl font-semibold text-slate-900 mb-3">Pipeline Stages Overview</h3>
          <div className="space-y-4">
            <div className="border-l-4 border-gray-500 bg-gray-50 p-4 rounded-r-lg">
              <h4 className="font-semibold text-slate-900 mb-2">1. New</h4>
              <p className="text-slate-700 text-sm mb-2">
                <strong>Definition:</strong> Leads that have just been added to your CRM, either manually or through import.
              </p>
              <p className="text-slate-600 text-sm"><strong>Next Steps:</strong></p>
              <ul className="list-disc pl-6 text-slate-600 text-sm space-y-1">
                <li>Enrich the lead with AI to gather company data</li>
                <li>Research the company and contact</li>
                <li>Prepare your outreach strategy</li>
              </ul>
            </div>

            <div className="border-l-4 border-blue-500 bg-blue-50 p-4 rounded-r-lg">
              <h4 className="font-semibold text-slate-900 mb-2">2. Contacted</h4>
              <p className="text-slate-700 text-sm mb-2">
                <strong>Definition:</strong> You've made initial contact via email, phone, or other channels.
              </p>
              <p className="text-slate-600 text-sm"><strong>Next Steps:</strong></p>
              <ul className="list-disc pl-6 text-slate-600 text-sm space-y-1">
                <li>Follow up if no response</li>
                <li>Schedule a discovery call</li>
                <li>Send additional information as requested</li>
              </ul>
            </div>

            <div className="border-l-4 border-indigo-500 bg-indigo-50 p-4 rounded-r-lg">
              <h4 className="font-semibold text-slate-900 mb-2">3. Qualified</h4>
              <p className="text-slate-700 text-sm mb-2">
                <strong>Definition:</strong> The lead meets your ICP criteria and has expressed genuine interest.
              </p>
              <p className="text-slate-600 text-sm"><strong>Qualification Criteria:</strong></p>
              <ul className="list-disc pl-6 text-slate-600 text-sm space-y-1">
                <li>Has budget for your solution</li>
                <li>Matches your target company size/industry</li>
                <li>Has decision-making authority</li>
                <li>Has a clear need or pain point</li>
                <li>Timeline for purchase is defined</li>
              </ul>
            </div>

            <div className="border-l-4 border-purple-500 bg-purple-50 p-4 rounded-r-lg">
              <h4 className="font-semibold text-slate-900 mb-2">4. Proposal</h4>
              <p className="text-slate-700 text-sm mb-2">
                <strong>Definition:</strong> You've sent a formal proposal or quote.
              </p>
              <p className="text-slate-600 text-sm"><strong>Next Steps:</strong></p>
              <ul className="list-disc pl-6 text-slate-600 text-sm space-y-1">
                <li>Schedule proposal review meeting</li>
                <li>Answer questions and objections</li>
                <li>Provide case studies or references</li>
                <li>Prepare for negotiation</li>
              </ul>
            </div>

            <div className="border-l-4 border-orange-500 bg-orange-50 p-4 rounded-r-lg">
              <h4 className="font-semibold text-slate-900 mb-2">5. Negotiation</h4>
              <p className="text-slate-700 text-sm mb-2">
                <strong>Definition:</strong> Actively negotiating terms, pricing, or contract details.
              </p>
              <p className="text-slate-600 text-sm"><strong>Next Steps:</strong></p>
              <ul className="list-disc pl-6 text-slate-600 text-sm space-y-1">
                <li>Involve decision-makers from both sides</li>
                <li>Address final objections</li>
                <li>Prepare contract documents</li>
                <li>Set closing timeline</li>
              </ul>
            </div>

            <div className="border-l-4 border-green-500 bg-green-50 p-4 rounded-r-lg">
              <h4 className="font-semibold text-slate-900 mb-2">6. Won 🎉</h4>
              <p className="text-slate-700 text-sm mb-2">
                <strong>Definition:</strong> Deal closed successfully! Contract signed and payment received.
              </p>
              <p className="text-slate-600 text-sm"><strong>Next Steps:</strong></p>
              <ul className="list-disc pl-6 text-slate-600 text-sm space-y-1">
                <li>Begin onboarding process</li>
                <li>Send welcome materials</li>
                <li>Schedule kickoff meeting</li>
                <li>Update CRM with deal value</li>
              </ul>
            </div>

            <div className="border-l-4 border-red-500 bg-red-50 p-4 rounded-r-lg">
              <h4 className="font-semibold text-slate-900 mb-2">7. Lost</h4>
              <p className="text-slate-700 text-sm mb-2">
                <strong>Definition:</strong> Opportunity lost to competitor or lead decided not to proceed.
              </p>
              <p className="text-slate-600 text-sm"><strong>Next Steps:</strong></p>
              <ul className="list-disc pl-6 text-slate-600 text-sm space-y-1">
                <li>Document the reason for loss</li>
                <li>Request feedback</li>
                <li>Add to nurture campaign for future opportunities</li>
                <li>Analyze what went wrong</li>
              </ul>
            </div>
          </div>
        </section>

        <section>
          <h3 className="text-xl font-semibold text-slate-900 mb-3">How to Update Lead Stages</h3>
          <ol className="list-decimal pl-6 space-y-2 text-slate-700">
            <li>Open the lead details page by clicking on the lead name</li>
            <li>Find the "Stage" dropdown field</li>
            <li>Select the new stage from the list</li>
            <li>The change is saved automatically</li>
            <li>An activity log entry is created tracking the stage change</li>
          </ol>
        </section>

        <section>
          <h3 className="text-xl font-semibold text-slate-900 mb-3">Conversion Funnel Analytics</h3>
          <p className="text-slate-700 mb-3">
            Track how leads move through your pipeline in the Analytics page:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-slate-700">
            <li><strong>Conversion Rates</strong>: See % moving from one stage to next</li>
            <li><strong>Average Time in Stage</strong>: Identify bottlenecks</li>
            <li><strong>Win Rate</strong>: Overall percentage of leads that become customers</li>
            <li><strong>Drop-off Analysis</strong>: See where you're losing the most leads</li>
          </ul>
        </section>

        <section>
          <h3 className="text-xl font-semibold text-slate-900 mb-3">Best Practices</h3>
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
            <ul className="space-y-2 text-slate-700 text-sm">
              <li className="flex items-start gap-2">
                <span className="text-indigo-600 font-bold">✓</span>
                <span><strong>Update stages promptly</strong> - Keep your pipeline current for accurate forecasting</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-indigo-600 font-bold">✓</span>
                <span><strong>Define clear criteria</strong> - Ensure your team agrees on what qualifies each stage</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-indigo-600 font-bold">✓</span>
                <span><strong>Don't skip stages</strong> - Follow the natural progression for better tracking</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-indigo-600 font-bold">✓</span>
                <span><strong>Review weekly</strong> - Regular pipeline reviews keep deals moving forward</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-indigo-600 font-bold">✓</span>
                <span><strong>Learn from losses</strong> - Analyze why deals were lost to improve</span>
              </li>
            </ul>
          </div>
        </section>
      </div>
    </DocLayout>
  );
};

export default PipelineStages;